import { formatMoney, cn } from '../../lib/format.js';
import { useReducedMotion } from '../../hooks/useReducedMotion.js';

/**
 * The gold strip under the banner.
 *
 * Every line on it is derived from a public setting, so nothing here can drift
 * out of step with what checkout actually does: the free-shipping figure is the
 * threshold the cart uses, and cash on delivery only appears while it is
 * switched on. With nothing to say, the strip does not render at all.
 *
 * The moving track is decorative and `aria-hidden`; the same lines are given to
 * assistive technology once, as a plain list, because a looping track repeats
 * each line several times to fill the width. Under reduced motion the track is
 * replaced by a static, centred row.
 */

// A run has to be wider than the widest viewport, or the loop shows its seam.
const MIN_PER_RUN = 8;

const marqueeItems = (settings = {}) => {
  const { shipping = {}, store = {}, tax = {} } = settings;
  const items = [];

  // The operator's announcement is left out on purpose: it already runs in the
  // gold bar above the header, and the same line twice in one screen reads as
  // a mistake.
  if (Number(shipping.freeThreshold) > 0) {
    items.push(`Free shipping on orders over ${formatMoney(shipping.freeThreshold)}`);
  }
  if (shipping.codEnabled) items.push('Cash on delivery available');
  if (tax.enabled && tax.inclusive) items.push('All prices include GST');
  if (store.tagline?.trim()) items.push(store.tagline.trim());

  return [...new Set(items)];
};

export const HomeMarquee = ({ settings }) => {
  const reduced = useReducedMotion();
  const items = marqueeItems(settings);

  if (!items.length) return null;

  const repeats = Math.max(1, Math.ceil(MIN_PER_RUN / items.length));
  const run = Array.from({ length: repeats }, () => items).flat();

  return (
    <section aria-label="Store notes" className="group relative overflow-hidden bg-brand text-white">
      <ul className="sr-only">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>

      {reduced ? (
        <div
          aria-hidden="true"
          className="mx-auto flex max-w-(--container-content) flex-wrap items-center justify-center gap-x-6 gap-y-1 px-4 py-3 sm:px-6"
        >
          {items.map((item) => (
            <MarqueeItem key={item} label={item} />
          ))}
        </div>
      ) : (
        <div aria-hidden="true" className="flex w-max rail-marquee" style={{ '--rail-duration': `${run.length * 6}s` }}>
          {[0, 1].map((copy) => (
            <div key={copy} className="flex shrink-0 items-center">
              {run.map((item, index) => (
                <MarqueeItem key={`${copy}-${index}`} label={item} className="px-6 py-3" />
              ))}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const MarqueeItem = ({ label, className }) => (
  <span className={cn('flex shrink-0 items-center gap-3 text-xs font-medium whitespace-nowrap', className)}>
    <span aria-hidden="true" className="text-[#f3dfb4]">
      ★
    </span>
    {label}
  </span>
);

export default HomeMarquee;
